import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Fixed banner shown while a platform admin is in "view as customer" preview.
 * Surfaces that admin tools are off, counts down to the auto-expiry, and offers
 * one-click exit. Rendered from PublicLayout; auto-exits when the clock runs out.
 *
 * Not in the i18n MIGRATED_FILES list — plain strings match the admin-only
 * AdminBar this pairs with.
 */
export default function PreviewBanner() {
  const user = useAuthStore((s) => s.user);
  const exitPreview = useAuthStore((s) => s.exitPreview);
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const active = !!user?.previewActive;
  const expiresAt = user?.previewExpiresAt ?? null;

  // Tick once a second so the countdown stays live.
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  async function handleExit() {
    if (exiting) return;
    setExiting(true);
    try {
      await exitPreview();
      navigate('/admin');
    } catch {
      setExiting(false);
    }
  }

  // Auto-exit when the preview lapses.
  const remainingMs = expiresAt !== null ? expiresAt - now : null;
  useEffect(() => {
    if (active && remainingMs !== null && remainingMs <= 0 && !exiting) {
      handleExit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, remainingMs, exiting]);

  if (!active) return null;

  let countdown = '';
  if (remainingMs !== null && remainingMs > 0) {
    const totalSec = Math.floor(remainingMs / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    countdown = `${m}:${String(s).padStart(2, '0')}`;
  }

  return (
    <div
      className="fixed inset-x-0 top-0 z-[70] flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-stone-900 shadow-md"
      role="status"
      aria-live="polite"
    >
      <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        Previewing as a customer — admin tools are off
        {countdown && <span className="ml-1 tabular-nums opacity-80">({countdown})</span>}
      </span>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="ml-2 inline-flex items-center gap-1 rounded-full bg-stone-900 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-stone-800 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-1 focus-visible:ring-offset-amber-500"
      >
        {exiting ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
        Exit preview
      </button>
    </div>
  );
}
